import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter } from '../Modal';
import { useToast } from '../Toast';
import { iso, fmt } from '../../utils/formatters';
import { computeAvailableQty } from '../../services/calculator';
import type { PositionModalProps, Position } from '../../types';

/**
 * Modal pour ajouter/éditer une position
 * Supporte: Achat, Vente, Mining, Staking
 */
export function PositionModal({ isOpen, onClose, token, position, onSave }: PositionModalProps) {
  const { t } = useTranslation();
  const [type, setType] = useState<'buy' | 'sell' | 'mining' | 'staking'>('buy');
  const [date, setDate] = useState<string>(iso(new Date()));
  const [price, setPrice] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [cost, setCost] = useState<number>(0);

  // Mining fields
  const [miningPool, setMiningPool] = useState<string>('');
  const [miningHashrate, setMiningHashrate] = useState<string>('');

  // Staking fields
  const [stakingApy, setStakingApy] = useState<string>('');
  const [stakingDuration, setStakingDuration] = useState<string>('');
  const [stakingCooldown, setStakingCooldown] = useState<string>('');
  const [stakingPlatform, setStakingPlatform] = useState<string>('');
  const [stakingIsLocked, setStakingIsLocked] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // V2.I.10 : moved from custom modal-overlay + useModalAccessibility hook
  // to <Modal> (BaseModal). Gains : focus trap on Tab, body scroll lock,
  // restore focus on close, portal-rendered, role=dialog/aria-modal=true.
  const toast = useToast();

  // Initialiser les valeurs si en mode édition
  useEffect(() => {
    if (position && isOpen) {
      setType(position.type || 'buy');
      setDate(position.date || iso(new Date()));
      setPrice(position.price?.toString() || '');
      setQuantity(position.quantity?.toString() || '');
      setNote(position.note || '');

      // Mining fields
      setMiningPool((position as any).mining_pool || '');
      setMiningHashrate((position as any).mining_hashrate?.toString() || '');

      // Staking fields
      setStakingApy((position as any).staking_apy?.toString() || '');
      setStakingDuration((position as any).staking_duration?.toString() || '');
      setStakingCooldown((position as any).staking_cooldown?.toString() || '');
      setStakingPlatform((position as any).staking_platform || '');
      setStakingIsLocked((position as any).staking_is_locked || false);
    } else if (isOpen) {
      // Reset pour nouvelle position
      setType('buy');
      setDate(iso(new Date()));
      setPrice('');
      setQuantity('');
      setNote('');
      setMiningPool('');
      setMiningHashrate('');
      setStakingApy('');
      setStakingDuration('');
      setStakingCooldown('');
      setStakingPlatform('');
      setStakingIsLocked(false);
    }
  }, [position, isOpen]);

  // Calculer le coût total
  useEffect(() => {
    const p = parseFloat(price) || 0;
    const q = parseFloat(quantity) || 0;
    setCost(p * q);
  }, [price, quantity]);

  const handleSave = async (): Promise<void> => {
    if (!price || !quantity || !date) {
      toast(t('modals.position.requiredFields', 'Price, quantity and date are required'), 'warning');
      return;
    }

    const numericPrice = parseFloat(price);
    const numericQty = parseFloat(quantity);

    if (!Number.isFinite(numericPrice) || numericPrice < 0) {
      toast(t('modals.position.invalidPrice', { defaultValue: 'Price must be a positive number.' }), 'warning');
      return;
    }
    if (!Number.isFinite(numericQty) || numericQty <= 0) {
      toast(t('modals.position.invalidQuantity', { defaultValue: 'Quantity must be greater than zero.' }), 'warning');
      return;
    }

    // Reject sells that exceed the available quantity. Without this check a
    // user can drive the FIFO/LIFO bookkeeping into a negative remaining
    // balance, silently breaking every downstream P&L number.
    if (type === 'sell') {
      const positions: Position[] | undefined = (token as { positions?: Position[] }).positions;
      if (!positions || positions.length === 0) {
        toast(
          t('modals.position.sellNoHoldings', {
            defaultValue: 'You have no positions to sell for this token.',
          }),
          'error'
        );
        return;
      }
      const available = computeAvailableQty(positions, position?.id || null);
      // Tiny epsilon to absorb floating-point error when a user types the
      // exact full available quantity ("sell everything").
      if (numericQty > available + 1e-9) {
        toast(
          t('modals.position.sellExceedsAvailable', {
            defaultValue: 'Cannot sell {{requested}}; only {{available}} available.',
            requested: fmt(numericQty),
            available: fmt(available),
          }),
          'error'
        );
        return;
      }
    }

    setIsSaving(true);

    const baseData: any = {
      type,
      date,
      price: parseFloat(price),
      quantity: parseFloat(quantity),
      note: note.trim()
    };

    // Add mining-specific fields
    if (type === 'mining') {
      if (miningPool) baseData.mining_pool = miningPool.trim();
      if (miningHashrate) baseData.mining_hashrate = parseFloat(miningHashrate);
    }

    // Add staking-specific fields
    if (type === 'staking') {
      if (stakingApy) baseData.staking_apy = parseFloat(stakingApy);
      if (stakingDuration) baseData.staking_duration = parseInt(stakingDuration);
      if (stakingCooldown) baseData.staking_cooldown = parseInt(stakingCooldown);
      if (stakingPlatform) baseData.staking_platform = stakingPlatform.trim();
      baseData.staking_is_locked = stakingIsLocked;

      // Calculate staking end date
      if (stakingDuration) {
        const endDate = new Date(date);
        endDate.setDate(endDate.getDate() + parseInt(stakingDuration));
        baseData.staking_end_date = endDate.toISOString().split('T')[0];
      }
    }

    try {
      await onSave(baseData);

      // Reset form
      setPrice('');
      setQuantity('');
      setNote('');
      setMiningPool('');
      setMiningHashrate('');
      setStakingApy('');
      setStakingDuration('');
      setStakingCooldown('');
      setStakingPlatform('');
      setStakingIsLocked(false);

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  // Title with icon — string prop on BaseModal so we render it as plain
  // text. The icon was previously inside a <span> in the manual header;
  // emojis stand in for the lucide icon to keep the visual cue.
  const modalTitle = position
    ? `✏️ ${t('modals.position.editTitle', 'Edit Position')}`
    : `➕ ${t('modals.position.newTitle', 'New Position')}`;

  const modalFooter = (
    <ModalFooter>
      <button className="btn-ghost" onClick={onClose}>
        {t('common.cancel', 'Cancel')}
      </button>
      <button onClick={handleSave} disabled={isSaving}>
        {isSaving ? (
          <>
            <span className="spinner-inline" aria-hidden="true" />
            {t('common.saving', 'Saving...')}
          </>
        ) : (
          position ? t('modals.position.update', 'Update') : t('modals.position.add', 'Add')
        )}
      </button>
    </ModalFooter>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="md"
      footer={modalFooter}
    >
        <div className="form-group">
          <label>Token</label>
          <input
            value={`${token?.displayName || ''} (${token?.id || ''})`}
            readOnly
          />
        </div>

        <div className="form-group">
          <label>{t('modals.position.type', 'Type')}</label>
          <select aria-label={t('modals.position.type', 'Type')} value={type} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setType(e.target.value as any)} autoFocus>
            <option value="buy">💰 {t('modals.position.buy', 'Buy')}</option>
            <option value="sell">💸 {t('modals.position.sell', 'Sell')}</option>
            <option value="mining">⛏️ Mining</option>
            <option value="staking">🔒 Staking</option>
          </select>
        </div>

        <div className="form-group">
          <label>{t('modals.position.date', 'Date')}</label>
          <input
            type="date"
            value={date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>{t('modals.position.priceUsd', 'Price USD')} {type === 'mining' && `(${t('modals.position.miningPriceNote', 'value at time of mining')})`}</label>
          <input
            type="number"
            step="0.00000001"
            value={price}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrice(e.target.value)}
            placeholder="0.00"
          />
        </div>

        <div className="form-group">
          <label>{t('modals.position.quantity', 'Quantity')}</label>
          <input
            type="number"
            step="0.00000001"
            value={quantity}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuantity(e.target.value)}
            placeholder="0.00"
          />
        </div>

        {/* Mining-specific fields */}
        {type === 'mining' && (
          <>
            <div style={{
              padding: '12px',
              background: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '8px',
              marginBottom: '16px',
              borderLeft: '3px solid var(--accent)'
            }}>
              <strong style={{ fontSize: '14px', color: 'var(--accent)' }}>⛏️ {t('modals.position.miningInfo', 'Mining Information')}</strong>
            </div>

            <div className="form-group">
              <label>{t('modals.position.miningPool', 'Mining pool (optional)')}</label>
              <input
                type="text"
                value={miningPool}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMiningPool(e.target.value)}
                placeholder="Ex: Ethermine, F2Pool, NiceHash"
              />
            </div>

            <div className="form-group">
              <label>{t('modals.position.hashrate', 'Hashrate (optional)')}</label>
              <input
                type="number"
                step="0.01"
                value={miningHashrate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMiningHashrate(e.target.value)}
                placeholder="Ex: 100 (MH/s or TH/s)"
              />
            </div>
          </>
        )}

        {/* Staking-specific fields */}
        {type === 'staking' && (
          <>
            <div style={{
              padding: '12px',
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '8px',
              marginBottom: '16px',
              borderLeft: '3px solid #10b981'
            }}>
              <strong style={{ fontSize: '14px', color: '#10b981' }}>🔒 {t('modals.position.stakingInfo', 'Staking Information')}</strong>
            </div>

            <div className="form-group">
              <label>{t('modals.position.apy', 'Annual APY (%)')}</label>
              <input
                type="number"
                step="0.01"
                value={stakingApy}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStakingApy(e.target.value)}
                placeholder="Ex: 5.5"
              />
            </div>

            <div className="form-group">
              <label>{t('modals.position.stakingDuration', 'Staking duration (days)')}</label>
              <input
                type="number"
                value={stakingDuration}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStakingDuration(e.target.value)}
                placeholder="Ex: 30, 90, 365"
              />
            </div>

            <div className="form-group">
              <label>{t('modals.position.cooldownPeriod', 'Cooldown period (days)')}</label>
              <input
                type="number"
                value={stakingCooldown}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStakingCooldown(e.target.value)}
                placeholder="Ex: 7, 14, 21"
              />
              <small style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px', display: 'block' }}>
                {t('modals.position.cooldownHelp', 'Wait time before release (no APY)')}
              </small>
            </div>

            <div className="form-group">
              <label>{t('modals.position.platform', 'Platform (optional)')}</label>
              <input
                type="text"
                value={stakingPlatform}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStakingPlatform(e.target.value)}
                placeholder="Ex: Binance, Kraken, Native"
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={stakingIsLocked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStakingIsLocked(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>{t('modals.position.lockedStaking', 'Locked staking')}</span>
              </label>
              <small style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px', display: 'block' }}>
                {t('modals.position.lockedStakingHelp', 'Check if you cannot withdraw before the end')}
              </small>
            </div>
          </>
        )}

        <div className="form-group">
          <label>{t('modals.position.note', 'Note (optional)')}</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
            placeholder={
              type === 'buy' ? t('modals.position.notePlaceholderBuy', 'Ex: Bought on Binance') :
              type === 'sell' ? t('modals.position.notePlaceholderSell', 'Ex: Sold on Coinbase') :
              type === 'mining' ? t('modals.position.notePlaceholderMining', 'Ex: Mined with RTX 3080') :
              t('modals.position.notePlaceholderStaking', 'Ex: Staking on Binance Earn')
            }
          />
        </div>

        <div className="cost-preview">
          <strong>
            {type === 'staking' ? t('modals.position.totalValue', 'Total value:') :
             type === 'mining' ? t('modals.position.minedValue', 'Mined value:') :
             t('modals.position.totalCost', 'Total cost:')}
          </strong> {fmt(cost, 2)} USD
        </div>
    </Modal>
  );
}
